import React, { useEffect, useId, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  MenuItem,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  BRAND_COLOURS,
  CHART_PALETTES,
  type ColourChoice,
  type Hsv,
  clampUnit,
  contrastRatio,
  hexToHsv,
  hsvToHex,
  hueAtPoint,
  normaliseHex,
} from './colourPickerModel';

type Props = {
  value?: ColourChoice;
  automaticColour: string;
  seriesColours: Array<string>;
  disabled?: boolean;
  onApply: (colour: ColourChoice) => void;
};

function Swatch({
  hex,
  label,
  selected,
  onPick,
}: {
  hex: string;
  label: string;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <Tooltip title={`${label} · ${hex.toUpperCase()}`}>
      <ButtonBase
        aria-label={`${label} ${hex}`}
        aria-pressed={selected}
        onClick={onPick}
        sx={{
          bgcolor: hex,
          width: '100%',
          aspectRatio: '1',
          minHeight: 32,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: selected ? '0 0 0 2px var(--mui-palette-background-paper, #fff), 0 0 0 4px #0f464f' : undefined,
          color: contrastRatio(hex, '#ffffff') > 3 ? '#ffffff' : '#0f0c08',
          transition: 'transform 120ms',
          '&:hover': { transform: 'translateY(-2px)' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
        }}
      >
        {selected ? <CheckIcon sx={{ fontSize: 19 }} /> : null}
      </ButtonBase>
    </Tooltip>
  );
}

function ColourWheel({ value, onChange }: { value: Hsv; onChange: (next: Hsv) => void }) {
  const hueColour = hsvToHex({ h: value.h, s: 1, v: 1 });
  const radians = (value.h * Math.PI) / 180;
  const updateHue = (event: React.PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    if (Math.hypot(x, y) < rect.width * 0.37) return;
    onChange({ ...value, h: hueAtPoint(x, y) });
  };
  const updateField = (event: React.PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    onChange({
      ...value,
      s: clampUnit((event.clientX - rect.left) / rect.width),
      v: 1 - clampUnit((event.clientY - rect.top) / rect.height),
    });
  };
  return (
    <Box sx={{ mx: 'auto', width: 224, maxWidth: '100%' }}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1', my: 1 }}>
        <Box
          aria-label="Colour wheel hue"
          aria-valuemax={359}
          aria-valuemin={0}
          aria-valuenow={Math.round(value.h) % 360}
          aria-valuetext={`${Math.round(value.h) % 360} degrees`}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 10 : 1;
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? 359
                  : ['ArrowRight', 'ArrowUp'].includes(event.key)
                    ? (value.h + step) % 360
                    : ['ArrowLeft', 'ArrowDown'].includes(event.key)
                      ? (value.h - step + 360) % 360
                      : undefined;
            if (next !== undefined) {
              event.preventDefault();
              onChange({ ...value, h: next });
            }
          }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            updateHue(event);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) updateHue(event);
          }}
          onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
          role="slider"
          tabIndex={0}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            touchAction: 'none',
            cursor: 'crosshair',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'conic-gradient(#ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
              maskImage: 'radial-gradient(transparent 58%, #000 59%)',
            },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: `${50 + Math.sin(radians) * 43}%`,
              top: `${50 - Math.cos(radians) * 43}%`,
              width: 17,
              height: 17,
              transform: 'translate(-50%, -50%)',
              bgcolor: hueColour,
              border: '3px solid #fff',
              borderRadius: '50%',
              boxShadow: '0 0 0 1px rgba(0,0,0,.45)',
              pointerEvents: 'none',
            }}
          />
        </Box>
        <Box
          aria-label="Saturation and brightness. Use left and right to change saturation, up and down to change brightness."
          onKeyDown={(event) => {
            const step = event.shiftKey ? 0.1 : 0.01;
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            onChange({
              ...value,
              s: clampUnit(value.s + (event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0)),
              v: clampUnit(value.v + (event.key === 'ArrowDown' ? -step : event.key === 'ArrowUp' ? step : 0)),
            });
          }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            updateField(event);
          }}
          onPointerMove={(event) => {
            if (event.currentTarget.hasPointerCapture(event.pointerId)) updateField(event);
          }}
          onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
          role="group"
          tabIndex={0}
          sx={{
            position: 'absolute',
            inset: '22%',
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent), ${hueColour}`,
            borderRadius: '5px',
            cursor: 'crosshair',
            touchAction: 'none',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: `${value.s * 100}%`,
              top: `${(1 - value.v) * 100}%`,
              width: 12,
              height: 12,
              border: '2px solid #fff',
              borderRadius: '50%',
              boxShadow: '0 0 0 1px #000',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography color="text.secondary" sx={{ fontSize: 12, width: 68 }}>
          Saturation
        </Typography>
        <Slider
          aria-label="Colour saturation"
          min={0}
          max={100}
          onChange={(_, next) => onChange({ ...value, s: Number(next) / 100 })}
          size="small"
          value={Math.round(value.s * 100)}
        />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography color="text.secondary" sx={{ fontSize: 12, width: 68 }}>
          Brightness
        </Typography>
        <Slider
          aria-label="Colour brightness"
          min={0}
          max={100}
          onChange={(_, next) => onChange({ ...value, v: Number(next) / 100 })}
          size="small"
          value={Math.round(value.v * 100)}
        />
      </Stack>
    </Box>
  );
}

export default function ColourPicker({ value, automaticColour, seriesColours, disabled = false, onApply }: Props) {
  const theme = useTheme();
  const tabId = useId();
  const effective =
    typeof value === 'number'
      ? seriesColours[(value - 1) % seriesColours.length]
      : value === 'muted'
        ? '#7b8586'
        : (normaliseHex(value ?? '') ?? automaticColour);
  const [mode, setMode] = useState('brand');
  const [palette, setPalette] = useState('vibrant');
  const [draft, setDraft] = useState(effective);
  const [hsv, setHsv] = useState(() => hexToHsv(effective));
  const [hexText, setHexText] = useState(effective);
  useEffect(() => {
    setDraft(effective);
    setHexText(effective);
    setHsv(hexToHsv(effective));
  }, [effective]);

  const choose = (hex: string) => {
    setDraft(hex);
    setHexText(hex);
    setHsv(hexToHsv(hex));
  };
  const move = (next: Hsv) => {
    const hex = hsvToHex(next);
    setHsv(next);
    setDraft(hex);
    setHexText(hex);
  };
  const validHex = normaliseHex(hexText);
  const onChart = theme.palette.mode === 'dark' ? '#12505a' : '#ffffff';
  const faint = contrastRatio(draft, onChart) < 3;
  const changed = draft.toLowerCase() !== effective.toLowerCase();
  const selectedPalette = CHART_PALETTES.find((entry) => entry.id === palette) ?? CHART_PALETTES[0];

  return (
    <Box
      data-lens-colour-picker
      sx={{
        width: '100%',
        minWidth: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            bgcolor: draft,
            border: 1,
            borderColor: 'divider',
            borderRadius: '8px',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {BRAND_COLOURS.find((entry) => entry.hex === draft)?.label ?? 'Selected colour'}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            {draft.toUpperCase()}
          </Typography>
        </Box>
        <Button
          aria-label="Use automatic series colour"
          disabled={disabled || value === undefined || value === null}
          onClick={() => onApply(null)}
          size="small"
          startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
          sx={{ fontSize: 12, minWidth: 0, textTransform: 'none' }}
        >
          Auto
        </Button>
      </Stack>
      <Tabs
        aria-label="Colour options"
        onChange={(_, next) => setMode(next)}
        value={mode}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, minWidth: 0, py: 0.75, px: 1, fontSize: 12.5, textTransform: 'none' },
        }}
      >
        {(
          [
            ['brand', 'LE brand'],
            ['palettes', 'More palettes'],
            ['custom', 'Custom'],
          ] as const
        ).map(([key, label]) => (
          <Tab aria-controls={`${tabId}-${key}-panel`} id={`${tabId}-${key}-tab`} key={key} label={label} value={key} />
        ))}
      </Tabs>
      <Box aria-labelledby={`${tabId}-${mode}-tab`} id={`${tabId}-${mode}-panel`} role="tabpanel" sx={{ p: 1.5 }}>
        {mode === 'brand' ? (
          <>
            <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1.5 }}>
              The Luxury Escapes brand colours.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1.25, p: 0.5 }}>
              {BRAND_COLOURS.map((entry) => (
                <Swatch
                  hex={entry.hex}
                  key={entry.hex}
                  label={entry.label}
                  onPick={() => choose(entry.hex)}
                  selected={draft === entry.hex}
                />
              ))}
            </Box>
          </>
        ) : null}
        {mode === 'palettes' ? (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              label="Chart palette"
              onChange={(event) => setPalette(event.target.value)}
              select
              size="small"
              value={palette}
            >
              {CHART_PALETTES.map((entry) => (
                <MenuItem key={entry.id} value={entry.id}>
                  {entry.label}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.25, p: 0.5 }}>
              {selectedPalette.colours.map((hex, index) => (
                <Swatch
                  hex={hex}
                  key={hex}
                  label={`${selectedPalette.label} ${index + 1}`}
                  onPick={() => choose(hex)}
                  selected={draft === hex}
                />
              ))}
            </Box>
          </Stack>
        ) : null}
        {mode === 'custom' ? (
          <>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              Choose a hue on the ring, then adjust the centre.
            </Typography>
            <ColourWheel onChange={move} value={hsv} />
          </>
        ) : null}
        <TextField
          error={!validHex}
          fullWidth
          helperText={!validHex ? 'Enter a hex colour, for example #0F464F.' : undefined}
          label="Hex colour"
          onBlur={() => {
            if (validHex) setHexText(validHex.toUpperCase());
          }}
          onChange={(event) => {
            setHexText(event.target.value);
            const hex = normaliseHex(event.target.value);
            if (hex) {
              setDraft(hex);
              setHsv(hexToHsv(hex));
            }
          }}
          size="small"
          slotProps={{ htmlInput: { maxLength: 7, spellCheck: false, 'aria-label': 'Hex colour' } }}
          sx={{ mt: 2 }}
          value={hexText}
        />
        <Box
          sx={{
            mt: 1.5,
            p: 1.25,
            bgcolor: onChart,
            border: 1,
            borderColor: 'divider',
            borderRadius: '7px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box aria-hidden sx={{ width: 45, height: 4, borderRadius: 1, bgcolor: draft, flexShrink: 0 }} />
          <Typography color="text.secondary" sx={{ fontSize: 12 }}>
            On your chart
          </Typography>
        </Box>
        {faint ? (
          <Typography role="status" sx={{ color: 'warning.main', fontSize: 12, lineHeight: 1.4, mt: 1 }}>
            This colour may be hard to see against the chart background.
          </Typography>
        ) : null}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1.5, py: 1.25, borderTop: 1, borderColor: 'divider', gap: 1 }}
      >
        <Typography aria-live="polite" color="text.secondary" sx={{ fontSize: 11.5 }}>
          {disabled
            ? 'Saving colour…'
            : changed
              ? 'Ready to apply'
              : value === undefined || value === null
                ? 'Using automatic colour'
                : 'Applied to this series'}
        </Typography>
        <Button
          disabled={disabled || !validHex || !changed}
          onClick={() => onApply(draft)}
          size="small"
          startIcon={disabled ? <CircularProgress color="inherit" size={13} /> : undefined}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap', px: 1.5 }}
          variant="contained"
        >
          Apply colour
        </Button>
      </Stack>
    </Box>
  );
}
